import React, { useState, useRef } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  CircleDot, Play, CheckCircle2, ShieldCheck, XCircle, Loader2,
  ArrowRight, Paperclip, Upload, X, Users,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Status definitions: value, label, icon, colors
export const STATUS_CONFIG = [
  {
    value: 'open',
    label: 'Open',
    sub: 'Not started yet',
    Icon: CircleDot,
    ring: 'ring-slate-400',
    bg: 'bg-slate-50 hover:bg-slate-100',
    sel: 'bg-slate-700 text-white ring-slate-700',
    badge: 'bg-slate-100 text-slate-700 border-slate-200',
  },
  {
    value: 'in_progress',
    label: 'In Progress',
    sub: 'Actively working',
    Icon: Play,
    ring: 'ring-blue-400',
    bg: 'bg-blue-50 hover:bg-blue-100',
    sel: 'bg-blue-600 text-white ring-blue-600',
    badge: 'bg-blue-100 text-blue-700 border-blue-200',
  },
  {
    value: 'pending_verification',
    label: 'Pending Review',
    sub: 'Awaiting verifier',
    Icon: ShieldCheck,
    ring: 'ring-amber-400',
    bg: 'bg-amber-50 hover:bg-amber-100',
    sel: 'bg-amber-600 text-white ring-amber-600',
    badge: 'bg-amber-100 text-amber-700 border-amber-200',
  },
  {
    value: 'closed',
    label: 'Closed',
    sub: 'Completed & done',
    Icon: CheckCircle2,
    ring: 'ring-emerald-400',
    bg: 'bg-emerald-50 hover:bg-emerald-100',
    sel: 'bg-emerald-600 text-white ring-emerald-600',
    badge: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  },
  {
    value: 'rejected',
    label: 'Rejected',
    sub: 'Sent back to owner',
    Icon: XCircle,
    ring: 'ring-red-400',
    bg: 'bg-red-50 hover:bg-red-100',
    sel: 'bg-red-600 text-white ring-red-600',
    badge: 'bg-red-100 text-red-600 border-red-200',
  },
];

export function StatusChip({ value, size = 'sm' }) {
  const cfg = STATUS_CONFIG.find(s => s.value === value);
  if (!cfg) return null;
  const { Icon, badge, label } = cfg;
  const sz = size === 'xs' ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-0.5';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border font-medium ${badge} ${sz}`}>
      <Icon className={size === 'xs' ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
      {label}
    </span>
  );
}

/**
 * StatusUpdatePopup
 *
 * Props:
 *  open          boolean
 *  onClose       () => void
 *  task          { task_id, title, status, verifier_id }
 *  currentUser   { id, role }
 *  onSubmit      (newStatus: string, remark: string) => Promise<void>
 *  allowedStatuses  string[] | undefined  – if undefined, shows all except current
 */
export default function StatusUpdatePopup({
  open,
  onClose,
  task,
  currentUser,
  onSubmit,
  allowedStatuses,
}) {
  const [selected, setSelected] = useState('');
  const [remark, setRemark]     = useState('');
  const [saving, setSaving]     = useState(false);
  const [file, setFile]         = useState(null);
  const fileRef                 = useRef();

  // Partner pending state (shown when in_progress is selected)
  const [partnerPending, setPartnerPending]           = useState(false);
  const [partnerEmail, setPartnerEmail]               = useState('');
  const [partnerMobile, setPartnerMobile]             = useState('');
  const [partnerLastContact, setPartnerLastContact]   = useState('');

  if (!task) return null;

  const needsAttachment = selected === 'pending_verification' && task.attachment_required;
  const showPartnerSection = selected === 'in_progress';

  const handleOpen = (isOpen) => {
    if (isOpen) {
      setSelected(''); setRemark(''); setFile(null);
      setPartnerPending(false); setPartnerEmail(''); setPartnerMobile(''); setPartnerLastContact('');
    }
  };

  const isVerifier = task.verifier_id && task.verifier_id === currentUser?.id;
  let available = STATUS_CONFIG.filter(s => s.value !== task.status);

  if (allowedStatuses) {
    available = available.filter(s => allowedStatuses.includes(s.value));
  } else if (task.status === 'pending_verification' && isVerifier) {
    available = available.filter(s => ['closed', 'rejected', 'in_progress'].includes(s.value));
  } else if (!['admin', 'sub_admin'].includes(currentUser?.role)) {
    const allowed = {
      // From open: allow starting work, submitting for review, or closing directly
      open:                 task.attachment_required
                              ? ['in_progress', 'pending_verification']
                              : ['in_progress', 'pending_verification', 'closed'],
      // allow direct close from in_progress only when no attachment required
      in_progress:          task.attachment_required
                              ? ['pending_verification', 'open']
                              : ['closed', 'pending_verification', 'open'],
      pending_verification: ['in_progress'],
      closed:               ['in_progress', 'open'],   // allow reopen
      rejected:             ['in_progress', 'open'],
    };
    available = available.filter(s => (allowed[task.status] || []).includes(s.value));
  }

  const handleSubmit = async () => {
    if (!selected) return;
    if (needsAttachment && !file) {
      // shake the upload zone via a state flash — just show toast
      import('sonner').then(({ toast }) => toast.error('Please attach a file before submitting'));
      return;
    }
    setSaving(true);
    try {
      // If attachment needed, upload first
      if (needsAttachment && file) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('task_id', task.task_id);
        fd.append('user_id', currentUser?.id || '');
        const up = await axios.post(`${API}/api/tasks/upload-attachment`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        await axios.post(`${API}/api/tasks/${task.task_id}/activity`, {
          requester_id: currentUser?.id,
          action_type: 'attachment',
          remark: `Attached: ${file.name}`,
          attachment_urls: [up.data],
        });
      }
      const partnerData = showPartnerSection && partnerPending ? {
        partner_pending: true,
        partner_email: partnerEmail.trim(),
        partner_mobile: partnerMobile.trim(),
        partner_last_contact: partnerLastContact,
      } : showPartnerSection ? { partner_pending: false } : {};
      await onSubmit(selected, remark.trim(), partnerData);
      onClose();
    } catch (e) {
      import('sonner').then(({ toast }) => toast.error(e?.response?.data?.detail || 'Failed'));
    } finally {
      setSaving(false);
    }
  };

  const fromCfg = STATUS_CONFIG.find(s => s.value === task.status);
  const toCfg   = STATUS_CONFIG.find(s => s.value === selected);

  return (
    <Dialog open={open} onOpenChange={(v) => { handleOpen(v); if (!v) onClose(); }}>
      <DialogContent className="max-w-md w-full p-0 gap-0 overflow-hidden rounded-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-5 py-4">
          <DialogHeader>
            <DialogTitle className="text-white text-sm font-semibold flex items-center gap-2">
              {task.status === 'closed' ? 'Reopen Task' : 'Update Task Status'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-300 text-[13px] mt-1 leading-snug line-clamp-2 font-medium">
            {task.title}
          </p>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-slate-400 text-xs">From:</span>
            {fromCfg && (
              <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium ${fromCfg.badge}`}>
                <fromCfg.Icon className="w-3 h-3" />
                {fromCfg.label}
              </span>
            )}
            {toCfg && (
              <>
                <ArrowRight className="w-3 h-3 text-slate-400" />
                <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium ${toCfg.badge}`}>
                  <toCfg.Icon className="w-3 h-3" />
                  {toCfg.label}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Status options */}
        <div className="px-5 pt-4 pb-3">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
            Move to
          </p>

          {available.length === 0 ? (
            <p className="text-sm text-slate-400 py-3 text-center">No status transitions available.</p>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {available.map(s => {
                const isSelected = selected === s.value;
                return (
                  <button
                    key={s.value}
                    onClick={() => setSelected(isSelected ? '' : s.value)}
                    data-testid={`status-option-${s.value}`}
                    className={`flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all text-left
                      ${isSelected
                        ? `border-current ring-2 ${s.ring} ${s.sel}`
                        : `border-transparent ${s.bg} text-slate-700`
                      }`}
                  >
                    <s.Icon className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-white' : ''}`} />
                    <div className="min-w-0">
                      <p className={`text-xs font-semibold leading-tight ${isSelected ? 'text-white' : 'text-slate-700'}`}>
                        {s.label}
                      </p>
                      <p className={`text-[10px] leading-tight mt-0.5 ${isSelected ? 'text-white/70' : 'text-slate-400'}`}>
                        {s.sub}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Remark */}
        <div className="px-5 pb-2">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">
            Remark <span className="normal-case font-normal text-slate-400">(optional)</span>
          </label>
          <Textarea
            value={remark}
            onChange={e => setRemark(e.target.value)}
            placeholder="Add a brief update or note…"
            rows={2}
            className="text-sm border-slate-200 resize-none focus:border-amber-400 focus:ring-amber-100 rounded-xl"
            data-testid="status-update-remark"
          />
        </div>

        {/* Partner Pending — shown only when In Progress is selected */}
        {showPartnerSection && (
          <div className="px-5 pb-3">
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => setPartnerPending(p => !p)}
                data-testid="partner-pending-toggle"
                className={`w-full flex items-center justify-between px-3.5 py-2.5 text-left transition-colors
                  ${partnerPending ? 'bg-orange-50 border-b border-orange-100' : 'bg-slate-50 hover:bg-slate-100'}`}
              >
                <div className="flex items-center gap-2">
                  <Users className={`w-4 h-4 ${partnerPending ? 'text-orange-600' : 'text-slate-400'}`} />
                  <span className={`text-xs font-semibold ${partnerPending ? 'text-orange-700' : 'text-slate-600'}`}>
                    Pending at Partner
                  </span>
                </div>
                <div className={`w-8 h-4 rounded-full transition-colors relative ${partnerPending ? 'bg-orange-500' : 'bg-slate-300'}`}>
                  <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-all duration-150 ${partnerPending ? 'left-4' : 'left-0.5'}`} />
                </div>
              </button>
              {partnerPending && (
                <div className="px-3.5 py-3 space-y-2.5 bg-orange-50/30">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide block mb-1">Partner Email</label>
                      <Input
                        type="email"
                        value={partnerEmail}
                        onChange={e => setPartnerEmail(e.target.value)}
                        placeholder="partner@example.com"
                        className="h-8 text-xs border-slate-200 rounded-lg"
                        data-testid="partner-email-input"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide block mb-1">Partner Mobile</label>
                      <Input
                        value={partnerMobile}
                        onChange={e => setPartnerMobile(e.target.value)}
                        placeholder="+91 98765 43210"
                        className="h-8 text-xs border-slate-200 rounded-lg"
                        data-testid="partner-mobile-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide block mb-1">Last Conversation Date</label>
                    <Input
                      type="date"
                      value={partnerLastContact}
                      onChange={e => setPartnerLastContact(e.target.value)}
                      className="h-8 text-xs border-slate-200 rounded-lg w-full"
                      data-testid="partner-last-contact-input"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Attachment — shown only when pending_verification + attachment_required */}
        {needsAttachment && (
          <div className="px-5 pb-4">
            <div className="flex items-center gap-1.5 mb-2">
              <Paperclip className="w-3.5 h-3.5 text-amber-600" />
              <label className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                Attachment Required
              </label>
            </div>
            <div
              onClick={() => fileRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors
                ${file
                  ? 'border-emerald-300 bg-emerald-50/40'
                  : 'border-amber-200 bg-amber-50/30 hover:border-amber-400 hover:bg-amber-50'
                }`}
              data-testid="attachment-drop-zone"
            >
              {file ? (
                <div className="flex items-center justify-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span className="text-xs font-medium text-slate-700 truncate max-w-[200px]">{file.name}</span>
                  <button
                    type="button"
                    onClick={e => { e.stopPropagation(); setFile(null); }}
                    className="text-slate-300 hover:text-red-400 flex-shrink-0"
                    data-testid="attachment-clear-btn"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="w-5 h-5 text-amber-400 mx-auto mb-1" />
                  <p className="text-xs text-amber-700 font-medium">Click to attach file</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">Images, PDFs, Excel — required to submit</p>
                </>
              )}
              <input
                ref={fileRef}
                type="file"
                className="hidden"
                onChange={e => setFile(e.target.files?.[0] || null)}
                data-testid="attachment-file-input"
              />
            </div>
          </div>
        )}

        {/* Footer */}
        <DialogFooter className="px-5 pb-4 flex items-center gap-2 border-t border-slate-100 pt-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="text-xs flex-1 h-9 border-slate-200 text-slate-600"
            data-testid="status-update-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selected || saving || (needsAttachment && !file)}
            className="text-xs flex-1 h-9 bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-40"
            data-testid="status-update-submit"
          >
            {saving ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />{needsAttachment && file ? 'Uploading…' : 'Saving…'}</>
            ) : needsAttachment && !file ? (
              <><Paperclip className="w-3.5 h-3.5 mr-1.5" />Attach file first</>
            ) : (
              <>Update Status <ArrowRight className="w-3.5 h-3.5 ml-1.5" /></>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
